import {
  IInpatientOutlierConfig,
  InpatientOutlierTrigger,
  InpatientOutlierFormula,
  InpatientOutlierMode,
} from "types/InpatientRuleset";

import { IInpatientRulesetFormData } from "../components/InpatientRulesetForm";

/**
 * Assemble an INPATIENT_OUTLIER config object from the flat form state.
 * Returns the config in the shape the backend expects (trigger + formula + mode).
 */
export function buildInpatientOutlierConfig(
  formData: IInpatientRulesetFormData
): IInpatientOutlierConfig {
  let trigger: InpatientOutlierTrigger;
  if (formData.ipoTriggerType === "STANDARD_CHARGE_THRESHOLD") {
    trigger = {
      type: "STANDARD_CHARGE_THRESHOLD",
      thresholdMethod: formData.ipoThresholdMethod,
      ...(formData.ipoThresholdMethod !== "FIXED"
        ? { baseReference: formData.ipoBaseReference }
        : {}),
      ...(formData.ipoThresholdMethod === "FIXED" ||
      formData.ipoThresholdMethod === "BASE_PLUS_FIXED"
        ? { fixedAmountInCents: formData.ipoFixedAmountInCents! }
        : {}),
      ...(formData.ipoThresholdMethod === "MULTIPLIER"
        ? { multiplier: formData.ipoMultiplier! }
        : {}),
      ...(formData.ipoChargeFloorInCents != null
        ? { chargeFloorInCents: formData.ipoChargeFloorInCents }
        : {}),
    };
  } else if (formData.ipoTriggerType === "MODIFIED_CHARGE_THRESHOLD") {
    trigger = {
      type: "MODIFIED_CHARGE_THRESHOLD",
      costToChargeRatio: formData.ipoCostToChargeRatio!,
      thresholdMethod: formData.ipoThresholdMethod,
      ...(formData.ipoThresholdMethod !== "FIXED"
        ? { baseReference: formData.ipoBaseReference }
        : {}),
      ...(formData.ipoThresholdMethod === "FIXED" ||
      formData.ipoThresholdMethod === "BASE_PLUS_FIXED"
        ? { fixedAmountInCents: formData.ipoFixedAmountInCents! }
        : {}),
      ...(formData.ipoThresholdMethod === "MULTIPLIER"
        ? { multiplier: formData.ipoMultiplier! }
        : {}),
      ...(formData.ipoChargeFloorInCents != null
        ? { chargeFloorInCents: formData.ipoChargeFloorInCents }
        : {}),
    };
  } else if (formData.ipoTriggerType === "LOS_RANGE") {
    trigger = {
      type: "LOS_RANGE",
      operator: formData.ipoLosOperator,
      days:
        formData.ipoLosDaysSource === "FEE_SCHEDULE"
          ? null
          : formData.ipoLosDays!,
      daysSource: formData.ipoLosDaysSource,
    };
  } else if (formData.ipoTriggerType === "PER_CODE_CHARGE_THRESHOLD") {
    trigger = {
      type: "PER_CODE_CHARGE_THRESHOLD",
      codeChargeFloorInCents: formData.ipoCodeChargeFloorInCents ?? 0,
      onlyCodesWithoutFeeSchedule: formData.ipoOnlyCodesWithoutFeeSchedule,
    };
  } else if (formData.ipoTriggerType === "DISCHARGE_STATUS") {
    trigger = {
      type: "DISCHARGE_STATUS",
      codes: formData.ipoDischargeStatusCodes,
    };
  } else if (formData.ipoTriggerType === "ADJUSTMENT_CODE") {
    // CLE-2303 — codes are normalized to the upper-cased GROUP-CODE form
    // ('co-74' → 'CO-74') so storage matches what the backend evaluator
    // expects without re-normalization at evaluation time.
    trigger = {
      type: "ADJUSTMENT_CODE",
      codes: formData.ipoAdjustmentCodes.map((c) => c.trim().toUpperCase()),
      ...(formData.ipoRequireDominancePercent != null
        ? { requireDominancePercent: formData.ipoRequireDominancePercent }
        : {}),
    };
  } else {
    trigger = { type: "ALWAYS" };
  }

  // Auto-couple per-diem source when trigger is LOS Range with FEE_SCHEDULE days source —
  // the form hides the per-diem-source dropdown in this case and we set it to the
  // (DRG rate ÷ ALOS) × outlier multiplier option (Rule 8 BCBST day outlier pattern).
  const triggerIsLosRangeFeeSchedule =
    formData.ipoTriggerType === "LOS_RANGE" &&
    formData.ipoLosDaysSource === "FEE_SCHEDULE";
  const effectivePerDiemSource = triggerIsLosRangeFeeSchedule
    ? "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER"
    : formData.ipoPerDiemSource;

  let formula: InpatientOutlierFormula;
  if (formData.ipoFormulaType === "PER_DIEM_X_LOS") {
    formula = {
      type: "PER_DIEM_X_LOS",
      perDiemSource: effectivePerDiemSource,
      ...(effectivePerDiemSource === "MANUAL"
        ? { manualPerDiemInCents: formData.ipoManualPerDiemInCents! }
        : {}),
      ...(formData.ipoLosAdjustment !== "NONE"
        ? { losAdjustment: formData.ipoLosAdjustment }
        : {}),
      ...(formData.ipoCapAtDrgRate ? { capAtDrgRate: true } : {}),
    };
  } else if (formData.ipoFormulaType === "PER_DIEM_X_EXCESS_LOS") {
    // Auto-couple excess base to trigger Days when trigger is LOS Range (CLE-2120 v3
    // form-UX simplification per Ani's Linear comment — trigger Days and formula Excess
    // LOS Base are always the same value for the documented rules).
    const useTriggerDays = formData.ipoTriggerType === "LOS_RANGE";
    const excessLosBaseSource = useTriggerDays
      ? formData.ipoLosDaysSource
      : formData.ipoExcessLosBaseSource;
    let excessLosBase: number | null;
    if (useTriggerDays) {
      excessLosBase =
        formData.ipoLosDaysSource === "FEE_SCHEDULE"
          ? null
          : formData.ipoLosDays!;
    } else {
      excessLosBase =
        formData.ipoExcessLosBaseSource === "FEE_SCHEDULE"
          ? null
          : formData.ipoExcessLosBase!;
    }
    formula = {
      type: "PER_DIEM_X_EXCESS_LOS",
      perDiemSource: effectivePerDiemSource,
      ...(effectivePerDiemSource === "MANUAL"
        ? { manualPerDiemInCents: formData.ipoManualPerDiemInCents! }
        : {}),
      excessLosBase,
      excessLosBaseSource,
    };
  } else if (formData.ipoFormulaType === "FLAT_RATE") {
    formula = {
      type: "FLAT_RATE",
      flatRateInCents: formData.ipoFlatRateInCents!,
    };
  } else if (formData.ipoFormulaType === "PERCENT_OF_CHARGES") {
    formula = { type: "PERCENT_OF_CHARGES", percent: formData.ipoPercent! };
  } else if (formData.ipoFormulaType === "PERCENT_OF_EXCESS_CHARGES") {
    formula = {
      type: "PERCENT_OF_EXCESS_CHARGES",
      percent: formData.ipoPercent!,
    };
  } else if (formData.ipoFormulaType === "DRG_FIELD_SUM") {
    formula = {
      type: "DRG_FIELD_SUM",
      fields: formData.ipoDrgFieldSumFields,
    };
  } else {
    formula = {
      type: "CASE_RATE_PLUS_PER_DIEM",
      caseRateInCents: formData.ipoCaseRateInCents!,
      includedDays: formData.ipoIncludedDays!,
      perDiemInCents: formData.ipoCaseRatePerDiemInCents!,
    };
  }

  const mode: InpatientOutlierMode = formData.ipoMode;
  return { trigger, formula, mode };
}

/**
 * Hydrate flat form fields from a stored INPATIENT_OUTLIER config.
 * Returns a partial form-data overlay; merge with INITIAL_FORM_DATA.
 */
export function hydrateInpatientOutlierFormData(
  config: IInpatientOutlierConfig
): Partial<IInpatientRulesetFormData> {
  const overlay: Partial<IInpatientRulesetFormData> = {};

  const { trigger } = config;
  overlay.ipoTriggerType = trigger.type;
  if (
    trigger.type === "STANDARD_CHARGE_THRESHOLD" ||
    trigger.type === "MODIFIED_CHARGE_THRESHOLD"
  ) {
    if (trigger.type === "MODIFIED_CHARGE_THRESHOLD") {
      overlay.ipoCostToChargeRatio = trigger.costToChargeRatio;
    }
    overlay.ipoThresholdMethod = trigger.thresholdMethod;
    if (trigger.baseReference) overlay.ipoBaseReference = trigger.baseReference;
    if (typeof trigger.fixedAmountInCents === "number")
      overlay.ipoFixedAmountInCents = trigger.fixedAmountInCents;
    if (typeof trigger.multiplier === "number")
      overlay.ipoMultiplier = trigger.multiplier;
    if (typeof trigger.chargeFloorInCents === "number")
      overlay.ipoChargeFloorInCents = trigger.chargeFloorInCents;
  } else if (trigger.type === "LOS_RANGE") {
    overlay.ipoLosOperator = trigger.operator;
    overlay.ipoLosDays = trigger.days;
    overlay.ipoLosDaysSource = trigger.daysSource;
  } else if (trigger.type === "PER_CODE_CHARGE_THRESHOLD") {
    overlay.ipoCodeChargeFloorInCents = trigger.codeChargeFloorInCents;
    overlay.ipoOnlyCodesWithoutFeeSchedule =
      trigger.onlyCodesWithoutFeeSchedule !== false;
  } else if (trigger.type === "DISCHARGE_STATUS") {
    overlay.ipoDischargeStatusCodes = trigger.codes;
  } else if (trigger.type === "ADJUSTMENT_CODE") {
    overlay.ipoAdjustmentCodes = trigger.codes;
    overlay.ipoRequireDominancePercent =
      typeof trigger.requireDominancePercent === "number"
        ? trigger.requireDominancePercent
        : null;
  }
  // ALWAYS trigger has no params to hydrate

  const { formula } = config;
  overlay.ipoFormulaType = formula.type;
  if (
    formula.type === "PER_DIEM_X_LOS" ||
    formula.type === "PER_DIEM_X_EXCESS_LOS"
  ) {
    overlay.ipoPerDiemSource = formula.perDiemSource;
    if (typeof formula.manualPerDiemInCents === "number")
      overlay.ipoManualPerDiemInCents = formula.manualPerDiemInCents;
    if (formula.type === "PER_DIEM_X_LOS") {
      overlay.ipoLosAdjustment = formula.losAdjustment ?? "NONE";
      overlay.ipoCapAtDrgRate = !!formula.capAtDrgRate;
    }
    if (formula.type === "PER_DIEM_X_EXCESS_LOS") {
      overlay.ipoExcessLosBase = formula.excessLosBase;
      overlay.ipoExcessLosBaseSource = formula.excessLosBaseSource;
    }
  } else if (formula.type === "FLAT_RATE") {
    overlay.ipoFlatRateInCents = formula.flatRateInCents;
  } else if (
    formula.type === "PERCENT_OF_CHARGES" ||
    formula.type === "PERCENT_OF_EXCESS_CHARGES"
  ) {
    overlay.ipoPercent = formula.percent;
  } else if (formula.type === "CASE_RATE_PLUS_PER_DIEM") {
    overlay.ipoCaseRateInCents = formula.caseRateInCents;
    overlay.ipoIncludedDays = formula.includedDays;
    overlay.ipoCaseRatePerDiemInCents = formula.perDiemInCents;
  } else if (formula.type === "DRG_FIELD_SUM") {
    overlay.ipoDrgFieldSumFields = formula.fields;
  }

  overlay.ipoMode = config.mode;
  return overlay;
}
